/**
 * Note: Pure AI slop :(
 */

import type { BusinessNameGeneratorStartPageContent } from '@/Shared/AutoGenerated/Main'
import type { ReactNode } from 'react'

interface BusinessNameGeneratorStepStartContentProps {
    appName: string
    content: BusinessNameGeneratorStartPageContent
}

interface ContentSectionProps {
    id: string
    title: string
    children: ReactNode
    className?: string
}

function ContentSection({
    id,
    title,
    children,
    className = '',
}: ContentSectionProps) {
    return (
        <section
            aria-labelledby={id}
            className={className}
        >
            <h2
                id={id}
                className="mb-4 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl"
            >
                {title}
            </h2>
            {children}
        </section>
    )
}

interface ContentLinkProps {
    href: string
    children: ReactNode
}

function ContentLink({ href, children }: ContentLinkProps) {
    return (
        <a
            href={href}
            className="font-medium text-primary underline underline-offset-2 hover:text-primary-shade"
        >
            {children}
        </a>
    )
}

interface NamingApproach {
    title: string
    description: string
}

function NamingApproachCard({ title, description }: NamingApproach) {
    return (
        <div>
            <h3 className="mb-2 text-base font-semibold text-gray-900 sm:text-lg">
                {title}
            </h3>
            <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                {description}
            </p>
        </div>
    )
}

interface NicheExample {
    name: string
    tagline: string
}

interface NicheItem {
    title: string
    prompt: string
    examples: NicheExample[]
}

function NicheExampleCard({ title, prompt, examples }: NicheItem) {
    return (
        <div>
            <h3 className="mb-2.5 text-base font-semibold text-gray-900 sm:text-lg">
                {title}
            </h3>

            <div className="mb-3.5 rounded-lg border border-primary/15 bg-blue-50/40 p-3 text-xs sm:p-3.5 sm:text-sm">
                <span className="font-semibold text-primary">
                    Example prompt to try:
                </span>{' '}
                <span className="text-gray-700">&ldquo;{prompt}&rdquo;</span>
            </div>

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
                {examples.map((example) => (
                    <div
                        key={example.name}
                        className="flex flex-col justify-center rounded-lg border border-stone-200 bg-white p-3.5 shadow-xs"
                    >
                        <span className="text-sm font-semibold text-gray-900">
                            {example.name}
                        </span>
                        <span className="mt-1 text-xs leading-relaxed text-gray-500">
                            {example.tagline}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

type FaqItem = BusinessNameGeneratorStartPageContent['faqItems'][number]

function FaqCard({ question, answer }: FaqItem) {
    return (
        <article className="rounded-xl border border-stone-200 bg-white p-5 shadow-xs sm:p-6">
            <h3 className="text-base font-semibold text-gray-900">
                {question}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
                {answer}
            </p>
        </article>
    )
}

const NAMING_APPROACHES: NamingApproach[] = [
    {
        title: '1. Use your own name',
        description:
            'Many solo experts trade under their own name, such as \u201CJane Smith Coaching\u201D or \u201CSmith Leadership Advisory\u201D. This builds personal authority immediately and keeps setup simple. The trade-off is that it ties the brand entirely to you, which can make it harder to expand into a group practice or sell the business later.',
    },
    {
        title: '2. Use a descriptive outcome or specialty name',
        description:
            'Names like \u201CTech Career Accelerator\u201D, \u201CCalm Mind Life Coaching\u201D, or \u201CB2B Sales Architecture\u201D explain exactly what you do or the transformation you provide. These names are immediately understood by potential clients, make word-of-mouth recommendations natural, and perform well in organic search.',
    },
    {
        title: '3. Use an evocative brand name',
        description:
            'Evocative names use metaphor or imagery to convey how clients feel working with you\u2014such as \u201CBeacon & Oak\u201D, \u201CNext Horizon\u201D, or \u201CSteady Path Advisory\u201D. These sound established from day one and are often easier to trademark. Because the name itself is broader, it works best when paired with a clear tagline that explains who you help\u2014which is why our generator creates a custom tagline alongside every name idea.',
    },
]

const NICHE_EXAMPLES: NicheItem[] = [
    {
        title: 'Life coach name ideas',
        prompt: "I'm a life coach helping burned-out professionals in their 30s and 40s reset their priorities, build calmer routines, and make confident life transitions.",
        examples: [
            {
                name: 'Clear Compass Coaching',
                tagline: 'Guidance for your next big life transition.',
            },
            {
                name: 'The Next Chapter Studio',
                tagline: 'Design a life that fits who you are now.',
            },
            {
                name: 'Steady Ground Life Coaching',
                tagline: 'Calm, practical coaching for busy professionals.',
            },
            {
                name: 'Fresh Horizon Coaching',
                tagline: 'Clarity and momentum when you feel stuck.',
            },
        ],
    },
    {
        title: 'Business coach name ideas',
        prompt: 'I coach bootstrapped agency owners and solo founders on pricing, client delivery systems, and growing past $20k/month without hiring a huge team.',
        examples: [
            {
                name: 'Growth Table Coaching',
                tagline: 'Practical strategy for profitable solo founders.',
            },
            {
                name: 'Founder Focus Co.',
                tagline: 'Build a calmer, higher-margin service business.',
            },
            {
                name: 'The Practical Operator',
                tagline: 'Systems and pricing coaching for agency owners.',
            },
            {
                name: 'Scaleway Business Coaching',
                tagline: 'Sustainable growth for independent businesses.',
            },
        ],
    },
    {
        title: 'Career coach name ideas',
        prompt: 'I help mid-career product managers and engineers prepare for senior interviews, negotiate offers, and pivot into leadership roles.',
        examples: [
            {
                name: 'Pivot Point Careers',
                tagline: 'Strategic career coaching for tech professionals.',
            },
            {
                name: 'Next Role Lab',
                tagline: 'Land your next senior role with confidence.',
            },
            {
                name: 'Career Clarity Studio',
                tagline: 'Focused coaching for high-stakes career moves.',
            },
            {
                name: 'The Offer Room',
                tagline:
                    'Interview preparation and salary negotiation coaching.',
            },
        ],
    },
    {
        title: 'Executive and leadership coach name ideas',
        prompt: "I'm an executive coach working with first-time VPs and directors at fast-growing companies to lead teams, communicate with boards, and make better decisions.",
        examples: [
            {
                name: 'Lead Well Advisory',
                tagline: 'Executive coaching for modern technical leaders.',
            },
            {
                name: 'Signal & Stone Leadership',
                tagline: 'Clear thinking for high-growth leadership teams.',
            },
            {
                name: 'Quiet Authority Coaching',
                tagline: 'Executive presence and leadership development.',
            },
            {
                name: 'Senior Seat Strategy',
                tagline: '1-on-1 advisory for new directors and VPs.',
            },
        ],
    },
    {
        title: 'Wellness and health coach name ideas',
        prompt: 'I help busy parents build sustainable nutrition, sleep, and strength habits without restrictive diets or two-hour gym sessions.',
        examples: [
            {
                name: 'Rooted Habits Coaching',
                tagline: 'Sustainable health habits for busy schedules.',
            },
            {
                name: 'Balanced Week Wellness',
                tagline: 'Practical nutrition and energy coaching.',
            },
            {
                name: 'Gentle Momentum Health',
                tagline: 'Small daily changes that actually stick.',
            },
            {
                name: 'Daily Foundations',
                tagline:
                    'Strength, sleep, and nutrition coaching for real life.',
            },
        ],
    },
    {
        title: 'Consultant name ideas (marketing, operations, strategy)',
        prompt: "I'm a B2B marketing and operations consultant helping seed-stage SaaS startups fix their positioning, onboarding funnel, and go-to-market execution.",
        examples: [
            {
                name: 'Sharp Edge Strategy',
                tagline:
                    'Positioning and go-to-market consulting for B2B teams.',
            },
            {
                name: 'Lean Loop Consulting',
                tagline: 'Streamlined operations for growing companies.',
            },
            {
                name: 'Clearline Operations',
                tagline: 'Process design and workflow consulting.',
            },
            {
                name: 'The Marketing Fixer',
                tagline: 'Hands-on funnel and messaging strategy.',
            },
        ],
    },
]

export function BusinessNameGeneratorStepStartContent({
    appName,
    content,
}: BusinessNameGeneratorStepStartContentProps) {
    return (
        <div className="mx-auto mt-12 max-w-3xl space-y-12 border-t border-stone-200 pt-12 pb-16 sm:space-y-16">
            {/* How it works */}
            <ContentSection
                id="how-it-works-heading"
                title="How the AI business name generator works"
            >
                <p className="mb-4 text-sm leading-relaxed text-gray-600 sm:text-base">
                    Naming a coaching or consulting business is harder than
                    naming a software product or online shop. You are often
                    selling your expertise, your method, and your client
                    outcomes at the same time. This free coaching and consulting
                    business name generator gives you a complete starting point
                    in three steps:
                </p>
                <ol className="mb-4 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-gray-600 sm:text-base">
                    <li>
                        <strong className="text-gray-900">
                            Describe your expertise.
                        </strong>{' '}
                        Write a sentence or two about what you do, who you help,
                        and the results your clients get. The more specific you
                        are, the more relevant your names will be.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Review tailored names and taglines.
                        </strong>{' '}
                        Get up to 10 custom business name ideas paired with
                        matching taglines and a starter session concept built
                        around your niche.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Preview and launch your booking page.
                        </strong>{' '}
                        Pick your favorite name to see an instant live preview
                        of how it looks on a professional{' '}
                        <ContentLink href="/">
                            {appName} booking page
                        </ContentLink>
                        , ready to accept discovery calls and client sessions.
                    </li>
                </ol>
                <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                    There is no signup required to generate names or preview
                    your page.
                </p>
            </ContentSection>

            {/* What makes a good name */}
            <ContentSection
                id="good-name-heading"
                title="What makes a good name for a coaching or consulting business?"
            >
                <p className="mb-4 text-sm leading-relaxed text-gray-600 sm:text-base">
                    The strongest names for coaches, consultants, and solo
                    experts share five practical traits:
                </p>
                <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-gray-600 sm:text-base">
                    <li>
                        <strong className="text-gray-900">
                            Clear about who you help or what changes.
                        </strong>{' '}
                        A name that hints at an outcome (&ldquo;clarity&rdquo;,
                        &ldquo;growth&rdquo;, &ldquo;momentum&rdquo;,
                        &ldquo;leadership&rdquo;) helps potential clients
                        understand your value right away.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Easy to say out loud.
                        </strong>{' '}
                        Most coaches and consultants win business through
                        referrals, podcasts, and conversations. If someone
                        cannot repeat your business name after hearing it once,
                        you lose word-of-mouth referrals.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Easy to spell and type.
                        </strong>{' '}
                        Avoid unusual spellings, hyphens, or clever respellings
                        that people will get wrong when they search for you or
                        type your booking link.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Flexible enough to grow with you.
                        </strong>{' '}
                        A name that is too narrow (&ldquo;Monday Morning Resume
                        Edits&rdquo;) can box you in when you expand from 1-on-1
                        coaching into group programs or advisory retainers.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Available to use.
                        </strong>{' '}
                        Check the domain name, social media handles, and your
                        local business registry before you commit.
                    </li>
                </ul>
            </ContentSection>

            {/* Three proven naming approaches */}
            <ContentSection
                id="naming-approaches-heading"
                title="Three proven naming approaches for solo experts"
            >
                <div className="space-y-6">
                    {NAMING_APPROACHES.map((approach) => (
                        <NamingApproachCard
                            key={approach.title}
                            title={approach.title}
                            description={approach.description}
                        />
                    ))}
                </div>
            </ContentSection>

            {/* Business name ideas by niche */}
            <ContentSection
                id="niche-examples-heading"
                title="Business name ideas by niche (with example prompts & taglines)"
            >
                <p className="mb-8 text-sm leading-relaxed text-gray-600 sm:text-base">
                    Use these examples for inspiration, or copy one of the
                    prompts into the generator above and tailor it to your
                    audience.
                </p>

                <div className="space-y-8">
                    {NICHE_EXAMPLES.map((niche) => (
                        <NicheExampleCard
                            key={niche.title}
                            title={niche.title}
                            prompt={niche.prompt}
                            examples={niche.examples}
                        />
                    ))}
                </div>
            </ContentSection>

            {/* 6 tips */}
            <ContentSection
                id="tips-heading"
                title="6 tips for choosing your final business name"
            >
                <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-gray-600 sm:text-base">
                    <li>
                        <strong className="text-gray-900">
                            Shortlist three to five names
                        </strong>
                        , then say each one out loud in a real introduction:{' '}
                        <span className="italic">
                            &ldquo;Hi, I run [Name]&mdash;I help [audience]
                            achieve [outcome].&rdquo;
                        </span>
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Search Google and social platforms.
                        </strong>{' '}
                        Look up each candidate name to make sure the search
                        results are not dominated by an existing competitor in
                        your space.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Check domain availability.
                        </strong>{' '}
                        A matching <code className="text-xs">.com</code> is
                        great, but a clean suffix like{' '}
                        <code className="text-xs">get[name].com</code>,{' '}
                        <code className="text-xs">[name]coaching.com</code>, or{' '}
                        <code className="text-xs">[name].co</code> works well
                        when your brand name is clear.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Test your shortlist with real clients.
                        </strong>{' '}
                        Show your top three options to a few people who match
                        your ideal client profile and ask which one sounds most
                        trustworthy.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Pair the name with a specific tagline.
                        </strong>{' '}
                        Your business name sets the tone; your tagline explains
                        the value. Together, they tell a visitor why they should
                        book a call with you.
                    </li>
                    <li>
                        <strong className="text-gray-900">
                            Check trademarks
                        </strong>{' '}
                        in your country&apos;s trademark database before
                        investing heavily in legal registration or custom
                        branding.
                    </li>
                </ol>
            </ContentSection>

            {/* Launch booking page */}
            <ContentSection
                id="launch-booking-page-heading"
                title="After you pick a name: launch your booking page"
            >
                <p className="mb-4 text-sm leading-relaxed text-gray-600 sm:text-base">
                    A business name only becomes real when clients can book time
                    with you. Instead of spending weeks building a complex
                    website before taking your first client, you can use your
                    chosen name and tagline to launch a{' '}
                    <ContentLink href="/">{appName} booking page</ContentLink>{' '}
                    in minutes&mdash;complete with your availability, one-on-one
                    and{' '}
                    <ContentLink href="/features/recurring-meetings">
                        recurring meetings
                    </ContentLink>
                    , automatic time zone detection, and calendar sync.
                </p>
                <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                    Want to explore more tools and scheduling strategies for
                    solo experts on the{' '}
                    <ContentLink href="/blog">{appName} blog</ContentLink>?
                    Check out our comparison of the{' '}
                    <ContentLink href="/blog/top-consulting-business-name-generators">
                        top consulting business name generators
                    </ContentLink>
                    , learn how to{' '}
                    <ContentLink href="/blog/make-more-sales-with-appointment-software-for-small-business">
                        make more sales with appointment scheduling software
                    </ContentLink>
                    , or read our guides on{' '}
                    <ContentLink href="/blog/what-the-40-20-40-rule-meetings">
                        the 40/20/40 rule for productive meetings
                    </ContentLink>{' '}
                    and{' '}
                    <ContentLink href="/blog/how-you-use-digital-calendar-effectively-complete-guide">
                        using a digital calendar effectively
                    </ContentLink>
                    .
                </p>
            </ContentSection>

            {/* Frequently asked questions */}
            <ContentSection
                id="faq-heading"
                title="Frequently asked questions"
            >
                <div className="space-y-4">
                    {content.faqItems.map((item) => (
                        <FaqCard
                            key={item.question}
                            question={item.question}
                            answer={item.answer}
                        />
                    ))}
                </div>
            </ContentSection>
        </div>
    )
}
